import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStoredToken } from '../services/auth';
import {
  CMSBlock,
  CMSFieldDef,
  CMSPageContent,
  CMSPageSchema,
  addCMSBlock,
  deleteCMSBlock,
  getCMSPage,
  getCMSSchema,
  reorderCMSBlocks,
  seedDomain,
  updateCMSBlock,
  updateCMSFields,
} from '../services/api';
import ContentBlockEditor from '../components/ContentBlockEditor';
import RichTextEditor from '../components/RichTextEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DOMAINS = ['net', 'ai', 'io', 'dev', 'manzoorulhassan'];
const PAGES = ['home', 'about', 'services', 'contact'];

const PAGE_LABEL: Record<string, Record<string, string>> = {
  manzoorulhassan: { services: 'Career & Skills' },
};

const getPageLabel = (domain: string, page: string) =>
  PAGE_LABEL[domain]?.[page] ?? page.charAt(0).toUpperCase() + page.slice(1);

const BLOCK_LABEL: Record<string, Record<string, Record<string, string>>> = {
  manzoorulhassan: { services: { services: 'Career Highlights' } },
};

const getBlockLabel = (domain: string, page: string, section: string, fallback: string) =>
  BLOCK_LABEL[domain]?.[page]?.[section] ?? fallback;

const AdminContent: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, isUserAdmin } = useAuth();

  const [selectedDomain, setSelectedDomain] = useState<string>(DOMAINS[0]);
  const [selectedPage, setSelectedPage] = useState<string>(PAGES[0]);

  const [schema, setSchema] = useState<CMSPageSchema | null>(null);
  const [content, setContent] = useState<CMSPageContent | null>(null);
  const [localFields, setLocalFields] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Font configuration state
  const [fontConfig, setFontConfig] = useState<{ fontFamily: string }>({ fontFamily: '' });
  const [fontSaving, setFontSaving] = useState(false);
  const [fontError, setFontError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin && !isUserAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, isUserAdmin, navigate]);

  const loadSchema = useCallback(async (pageName: string) => {
    const res = await getCMSSchema(pageName);
    if (res.data) setSchema(res.data);
  }, []);

  const loadContent = useCallback(async (domain: string, pageName: string) => {
    setLoading(true);
    const token = getStoredToken() ?? undefined;
    const res = await getCMSPage(pageName, domain, token);
    if (res.data) {
      setContent(res.data);
      const flat: Record<string, string> = {};
      for (const [k, v] of Object.entries(res.data.fields)) {
        flat[k] = v ?? '';
      }
      setLocalFields(flat);
    } else if (res.error) {
      setError(`Failed to load content: ${res.error}`);
      setMessage(null);
    }
    setLoading(false);
  }, []);

  // Load font configuration - but we'll handle it more gracefully
  const [fontConfigLoaded, setFontConfigLoaded] = useState(false);

  const loadFontConfig = useCallback(async (domain: string) => {
    try {
      const token = getStoredToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/cms/domains/${domain}/font-config`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFontConfig({ fontFamily: data.fontFamily || '' });
      }
    } catch (err) {
      // Silently fail - this is not critical for the main functionality
      // The font config section will still be visible with empty values
      console.warn('Failed to load font config (this is expected if endpoint does not exist yet):', err);
    } finally {
      setFontConfigLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadSchema(selectedPage);
    loadContent(selectedDomain, selectedPage);
    loadFontConfig(selectedDomain);
  }, [selectedDomain, selectedPage, loadSchema, loadContent, loadFontConfig]);

  const showMessage = (msg: string) => {
    setMessage(msg);
    setError(null);
    setTimeout(() => setMessage(null), 3000);
  };

  const showError = (msg: string) => {
    setError(msg);
    setMessage(null);
  };

  const handleSaveFields = async () => {
    const token = getStoredToken();
    if (!token) return;
    setSaving(true);
    const res = await updateCMSFields(selectedDomain, selectedPage, localFields, token);
    setSaving(false);
    if (res.error) {
      showError(`Failed to save fields: ${res.error}`);
    } else {
      showMessage('Fields saved.');
      if (res.data) {
        setContent(res.data);
      }
    }
  };

  const handleAddBlock = async (sectionName: string, blockFields: CMSFieldDef[]) => {
    const token = getStoredToken();
    if (!token) return;
    const emptyFields: Record<string, string> = {};
    blockFields.forEach((f) => { emptyFields[f.name] = ''; });
    const res = await addCMSBlock(selectedDomain, selectedPage, sectionName, emptyFields, token);
    if (res.error) {
      showError(`Failed to add block: ${res.error}`);
    } else {
      await loadContent(selectedDomain, selectedPage);
      showMessage('Block added.');
    }
  };

  const handleSaveBlock = async (blockId: number, fields: Record<string, any>) => {
    const token = getStoredToken();
    if (!token) return;
    const res = await updateCMSBlock(selectedDomain, selectedPage, blockId, fields, token);
    if (res.error) showError(`Failed to save block: ${res.error}`);
    else await loadContent(selectedDomain, selectedPage);
  };

  const handleDeleteBlock = async (blockId: number) => {
    const token = getStoredToken();
    if (!token) return;
    const res = await deleteCMSBlock(selectedDomain, selectedPage, blockId, token);
    if (res.error) {
      showError(`Failed to delete block: ${res.error}`);
    } else {
      await loadContent(selectedDomain, selectedPage);
      showMessage('Block deleted.');
    }
  };

  const handleMoveBlock = async (sectionName: string, blocks: CMSBlock[], blockId: number, direction: 'up' | 'down') => {
    const token = getStoredToken();
    if (!token) return;
    const ids = blocks.map((b) => b.id);
    const idx = ids.indexOf(blockId);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === ids.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    const res = await reorderCMSBlocks(selectedDomain, selectedPage, sectionName, ids, token);
    if (res.error) showError(`Failed to reorder: ${res.error}`);
    else await loadContent(selectedDomain, selectedPage);
  };

  const handleSeed = async () => {
    if (!window.confirm(
      `Seed "${selectedDomain}" from the _intrnauts template?\n\nThis only fills empty fields — it will not overwrite existing content.`
    )) return;
    const token = getStoredToken();
    if (!token) return;
    const res = await seedDomain(selectedDomain, '_intrnauts', token);
    if (res.error) {
      showError(`Seed failed: ${res.error}`);
    } else {
      showMessage(res.data?.message ?? 'Domain seeded.');
      await loadContent(selectedDomain, selectedPage);
    }
  };

  // Handle font configuration save
  const handleSaveFontConfig = async () => {
    try {
      const token = getStoredToken();
      if (!token) return;

      setFontSaving(true);
      setFontError(null);

      const response = await fetch(`${API_URL}/api/cms/domains/${selectedDomain}/font-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fontFamily: fontConfig.fontFamily })
      });

      if (response.ok) {
        showMessage('Font configuration saved successfully!');
        // Reload font config to show the updated value
        loadFontConfig(selectedDomain);
      } else {
        const errorData = await response.json();
        setFontError(errorData.detail || 'Failed to save font configuration');
      }
    } catch (err) {
      setFontError('Failed to save font configuration');
      console.error('Font config save error:', err);
    } finally {
      setFontSaving(false);
    }
  };

  if (!isAdmin && !isUserAdmin) return null;

  const noticeStyle: React.CSSProperties = {
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  };

  return (
    <div className="page-container">
      <div className="admin-container">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1>Content Management</h1>
          <button
            onClick={handleSeed}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Seed from Template
          </button>
        </div>

        {message && <div style={{ ...noticeStyle, backgroundColor: '#efe', border: '1px solid #cfc', color: '#060' }}>{message}</div>}
        {error && <div style={{ ...noticeStyle, backgroundColor: '#fee', border: '1px solid #fcc', color: '#c00' }}>{error}</div>}
        {fontError && <div style={{ ...noticeStyle, backgroundColor: '#fee', border: '1px solid #fcc', color: '#c00' }}>{fontError}</div>}

        {/* Selectors */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Domain</label>
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Page</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {PAGES.map((p) => (
                <button
                  key={p}
                  onClick={() => setSelectedPage(p)}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedPage === p ? '#007bff' : 'white',
                    color: selectedPage === p ? 'white' : '#333',
                  }}
                >
                  {getPageLabel(selectedDomain, p)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && !fontConfigLoaded ? (
          <div className="loading">Loading content…</div>
        ) : (
          <>
            {/* Font Configuration Section */}
            <div className="admin-section" style={{ marginBottom: '2rem', backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
              <h2 style={{ marginBottom: '1rem' }}>Font Configuration</h2>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                  Font Family
                </label>
                <input
                  type="text"
                  value={fontConfig.fontFamily}
                  onChange={(e) => setFontConfig({ ...fontConfig, fontFamily: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
                  placeholder="Enter font family (e.g., 'Segoe UI', 'Times New Roman', 'Roboto')"
                />
                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  <p>Current font preview: <span style={{ fontFamily: fontConfig.fontFamily || 'inherit' }}>The quick brown fox jumps over the lazy dog</span></p>
                  <p>Recommended fonts: Segoe UI, Times New Roman, Roboto, Open Sans, Lato, Montserrat</p>
                </div>
              </div>
              <button
                onClick={handleSaveFontConfig}
                disabled={fontSaving}
                style={{ padding: '0.5rem 1.25rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                {fontSaving ? 'Saving…' : 'Save Font Configuration'}
              </button>
            </div>

            {/* Scalar fields */}
            {schema && schema.scalar_fields.length > 0 && (
              <div className="admin-section" style={{ marginBottom: '2rem' }}>
                <h2 style={{ marginBottom: '1rem' }}>Page Fields</h2>
                {schema.scalar_fields.map((field) => (
                  <div key={field.name} style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <RichTextEditor
                        key={`${selectedDomain}-${selectedPage}-${field.name}`}
                        content={localFields[field.name] ?? ''}
                        onChange={(html) => setLocalFields((prev) => ({ ...prev, [field.name]: html }))}
                        minHeight={150}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={localFields[field.name] ?? ''}
                        onChange={(e) => setLocalFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        style={{ width: '100%', padding: '0.5rem', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
                      />
                    )}
                  </div>
                ))}
                <button
                  onClick={handleSaveFields}
                  disabled={saving}
                  style={{ padding: '0.5rem 1.25rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  {saving ? 'Saving…' : 'Save Fields'}
                </button>
              </div>
            )}

            {/* Block sections */}
            {schema && Object.entries(schema.block_sections).map(([sectionName, sectionDef]) => {
              const blocks = content?.blocks[sectionName] ?? [];
              return (
                <div key={sectionName} className="admin-section" style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2>{getBlockLabel(selectedDomain, selectedPage, sectionName, sectionDef.label)}</h2>
                    <button
                      onClick={() => handleAddBlock(sectionName, sectionDef.block_fields)}
                      style={{ padding: '0.4rem 0.875rem', fontSize: '0.875rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      + Add Block
                    </button>
                  </div>

                  {blocks.length === 0 ? (
                    <p style={{ color: '#888', fontSize: '0.875rem' }}>No blocks yet. Add one to get started.</p>
                  ) : (
                    blocks.map((block, idx) => (
                      <ContentBlockEditor
                        key={block.id}
                        blockId={block.id}
                        blockFields={sectionDef.block_fields}
                        values={block.fields}
                        onSave={handleSaveBlock}
                        onDelete={handleDeleteBlock}
                        onMoveUp={(id) => handleMoveBlock(sectionName, blocks, id, 'up')}
                        onMoveDown={(id) => handleMoveBlock(sectionName, blocks, id, 'down')}
                        isFirst={idx === 0}
                        isLast={idx === blocks.length - 1}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminContent;
